type AudioWsClientOpts = {
  wsUrl: string; // Kept for backwards compatibility in args, though unused
  onPartialTranscript?: (t: string) => void;
  onFinalTranscript?: (t: string) => void;
  onFullTranscript?: (t: string) => void;
  onError?: (e: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onLiveInsight?: (msg: any) => void;
};

export class AudioWsClient {
  private stream?: MediaStream;
  private audioCtx?: AudioContext;
  private processor?: ScriptProcessorNode;
  private mediaRecorder?: MediaRecorder;
  private recordedBlobs: Blob[] = [];
  private isRecording = false;

  constructor(private opts: AudioWsClientOpts) { }

  async start(consent: boolean, deviceId?: string) {
    if (!consent) throw new Error("Consent required");

    this.isRecording = true;

    // 1. Get microphone access for local recording
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: deviceId ? { exact: deviceId } : undefined,
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      // Init AudioContext for potential local processing / visualization
      this.audioCtx = new AudioContext({ sampleRate: 16000 });
      const source = this.audioCtx.createMediaStreamSource(this.stream);
      this.processor = this.audioCtx.createScriptProcessor(4096, 1, 1);
      source.connect(this.processor);
      this.processor.connect(this.audioCtx.destination);

      // MediaRecorder for high-quality final save
      // Codec priority: FLAC > WebM/Opus (desktop) > WebM > MP4/AAC (iOS Safari fallback)
      const mimeType = MediaRecorder.isTypeSupported('audio/flac')
        ? 'audio/flac'
        : MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : MediaRecorder.isTypeSupported('audio/webm')
            ? 'audio/webm'
            : MediaRecorder.isTypeSupported('audio/mp4;codecs=mp4a.40.2')
              ? 'audio/mp4;codecs=mp4a.40.2'
              : MediaRecorder.isTypeSupported('audio/mp4')
                ? 'audio/mp4'
                : '';

      this.mediaRecorder = mimeType
        ? new MediaRecorder(this.stream, { mimeType })
        : new MediaRecorder(this.stream);
      this.recordedBlobs = [];
      this.mediaRecorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
              this.recordedBlobs.push(event.data);
          }
      };
      this.mediaRecorder.start(1000); // Collect every second

      // Simulate "WebSocket opened" so the UI transitions to "Recording"
      setTimeout(() => {
        if (this.isRecording) {
            this.opts.onOpen?.();
        }
      }, 100);

    } catch (err) {
      console.error("Mic access failed", err);
      this.opts.onError?.("Microphone access denied");
      this.isRecording = false;
      return;
    }
  }

  // Pause recording without stopping the mic stream or MediaRecorder session.
  // This keeps a single continuous webm file across pause/resume cycles.
  pause(): void {
    this.isRecording = false;
    if (this.mediaRecorder?.state === 'recording') {
      this.mediaRecorder.pause();
    }
    setTimeout(() => this.opts.onClose?.(), 200);
  }

  // Resume a previously paused recording.
  resume(): void {
    this.isRecording = true;
    if (this.mediaRecorder?.state === 'paused') {
      this.mediaRecorder.resume();
    }
    setTimeout(() => {
      if (this.isRecording) this.opts.onOpen?.();
    }, 100);
  }

  isPaused(): boolean {
    return this.mediaRecorder?.state === 'paused';
  }

  // Fully stop recording and release the microphone.
  stop(): Promise<void> {
    this.isRecording = false;

    // Stop audio processing
    this.processor?.disconnect();
    if (this.audioCtx && this.audioCtx.state !== 'closed') {
      this.audioCtx.close().catch(() => {});
    }
    this.audioCtx = undefined;
    this.stream?.getTracks().forEach((t) => t.stop());

    return new Promise<void>((resolve) => {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.addEventListener('stop', () => {
          setTimeout(() => this.opts.onClose?.(), 200);
          resolve();
        }, { once: true });
        this.mediaRecorder.stop();
      } else {
        setTimeout(() => this.opts.onClose?.(), 200);
        resolve();
      }
    });
  }

  getRecordedAudio(): Blob {
      return new Blob(this.recordedBlobs, { type: this.mediaRecorder?.mimeType || 'audio/webm' });
  }

  // No-ops for obsolete WS methods
  sendJson() {
      // Disabled since WebSocket is removed
  }

  getStream() {
    return this.stream ?? null;
  }
}
